import React, { type PropsWithChildren } from "react";

import clsx from "clsx";

import { GenericTooltip } from "@components/tooltip-container";
import { useUser } from "@components/user-context";
import {
  type FetchBenchmarkSettings_me_benchmarks_benchmarkSettings_customBenchmarks,
  type FetchPlatformBenchmarks_me_benchmarks_platformBenchmarks,
  type BenchmarkAttributionWindowEnum,
  type BenchmarkPlatformEnum,
  BenchmarkTypeEnum,
} from "@north-beam/nb-client-side";
import { AdIcon } from "@pages/objects/utils";

import {
  BenchmarkPlatformAdIconSalesPageV3,
  PrettyBenchmarkAttributionWindowNamesSalesPageV3,
} from "../constants";
import { getPrettyBenchmark } from "../utils";

import { NoDataNotification } from "./notification";

export type BenchmarkCardProps = {
  title: string;
  platform: BenchmarkPlatformEnum;
  cac:
    | FetchPlatformBenchmarks_me_benchmarks_platformBenchmarks["cac"]
    | FetchBenchmarkSettings_me_benchmarks_benchmarkSettings_customBenchmarks["cac"];
  roas:
    | FetchPlatformBenchmarks_me_benchmarks_platformBenchmarks["roas"]
    | FetchBenchmarkSettings_me_benchmarks_benchmarkSettings_customBenchmarks["roas"];
  loading: boolean;
  ignoreNoData?: boolean;
  showNbCalculatedBenchmarks?: boolean;
  nbCalculatedBenchmarks?: FetchPlatformBenchmarks_me_benchmarks_platformBenchmarks;
  target: string;
  benchmarkType: BenchmarkTypeEnum;
  attributionWindow: BenchmarkAttributionWindowEnum;
};

const TableCell = ({
  children,
  className,
}: PropsWithChildren<{ className?: string }>) => (
  <div className={clsx("table-cell", className)}>{children}</div>
);

const HeaderTableCell = ({
  children,
  className,
}: PropsWithChildren<{ className?: string }>) => {
  return (
    <TableCell
      className={clsx("font-semibold text-xs text-right rounded-lg", className)}
    >
      {children}
    </TableCell>
  );
};

const BodyTableCell = ({
  children,
  className,
}: PropsWithChildren<{ className?: string }>) => (
  <TableCell className={clsx("py-3 px-2", className)}>{children}</TableCell>
);

const BenchmarkTableCell = ({
  benchmarkType,
  customerType,
  benchmark,
  showCustomBenchmarks,
  nbCalculatedBenchmarks,
  className,
}: {
  customerType: "firstTime" | "blended" | "returning";
  showCustomBenchmarks: boolean;
  benchmarkType: BenchmarkTypeEnum;
  benchmark: number | null | undefined;
  nbCalculatedBenchmarks?: FetchPlatformBenchmarks_me_benchmarks_platformBenchmarks;
  className?: string;
}) => {
  const { user } = useUser();

  return (
    <BodyTableCell className={className}>
      <div>{getPrettyBenchmark(benchmarkType, benchmark, user)}</div>
      {showCustomBenchmarks && nbCalculatedBenchmarks && (
        <div className="text-[11px] leading-3 font-light">
          (
          {getPrettyBenchmark(
            benchmarkType,
            nbCalculatedBenchmarks?.[
              benchmarkType === "CAC" ? "cac" : "roas"
            ]?.[customerType],
            user,
          )}
          )
        </div>
      )}
    </BodyTableCell>
  );
};

export const BenchmarkCard = ({
  title,
  platform,
  cac,
  roas,
  loading,
  ignoreNoData,
  nbCalculatedBenchmarks,
  showNbCalculatedBenchmarks = false,
  target,
  benchmarkType,
  attributionWindow,
}: BenchmarkCardProps) => {
  const dataUnavailable =
    !loading &&
    !cac?.blended &&
    !cac?.returning &&
    !cac?.firstTime &&
    !roas?.blended &&
    !roas?.returning &&
    !roas?.firstTime;
  const isGreyedOut = loading || dataUnavailable;
  return (
    <div className="w-full relative">
      {!ignoreNoData && dataUnavailable && (
        <NoDataNotification className="z-[1]" />
      )}
      <div className={clsx("flex mb-2 text-sm", { "opacity-25": isGreyedOut })}>
        <div className="flex-1 flex items-center">
          <AdIcon
            sizeInEM={1.8}
            platform={BenchmarkPlatformAdIconSalesPageV3[platform]}
            style={{ marginRight: "5px" }}
          />
          <span className="font-bold">{title}</span>
        </div>
        <div className="">
          <span className="bg-interstellar-3 rounded py-[6px] px-2 mr-1">
            {
              PrettyBenchmarkAttributionWindowNamesSalesPageV3[
                attributionWindow
              ]
            }
          </span>
          {benchmarkType} target <span className="font-bold">{target}</span>
        </div>
      </div>
      <div className="border border-gray rounded-lg">
        <div
          className={clsx("table border-collapse w-full mb-0", {
            "opacity-25": isGreyedOut,
          })}
        >
          <div className="table-header-group rounded-lg border-b-[1px] border-gray-3">
            <div className="table-row">
              <HeaderTableCell>
                <div className="border-r border-gray-1 px-2 py-[6px] my-2">
                  Metric{" "}
                  <GenericTooltip content="The unit for measuring your performance over time" />
                </div>
              </HeaderTableCell>
              <HeaderTableCell>
                <div className="border-r border-gray-1 px-2 py-1 my-2">
                  Blended{" "}
                  <GenericTooltip
                    content={
                      "Weighted average of first-time and " +
                      "returning attribution"
                    }
                  />
                </div>
              </HeaderTableCell>
              <HeaderTableCell>
                <div className="border-r border-gray-1 px-2 py-1 my-2">
                  First Time{" "}
                  <GenericTooltip content="Measurement for first-time customer attribution" />
                </div>
              </HeaderTableCell>
              <HeaderTableCell>
                <div className="border-r border-white px-2 py-1 my-2">
                  Returning{" "}
                  <GenericTooltip content="Measurement for returning customer attribution" />
                </div>
              </HeaderTableCell>
            </div>
          </div>
          <div className="table-row-group border-b-[1px] border-gray-1 bg-[#FAFAFF] text-sm">
            <BodyTableCell className="lext-left font-bold border-r border-gray-1">
              <div>CAC</div>
              {showNbCalculatedBenchmarks && (
                <div className="text-[11px] leading-3 font-light">
                  (NB Benchmark CAC)
                </div>
              )}
            </BodyTableCell>
            <BenchmarkTableCell
              className="text-right border-r border-gray-1"
              benchmark={cac?.blended}
              customerType="blended"
              showCustomBenchmarks={showNbCalculatedBenchmarks}
              benchmarkType={BenchmarkTypeEnum.CAC}
              nbCalculatedBenchmarks={nbCalculatedBenchmarks}
            />
            <BenchmarkTableCell
              className="text-right border-r border-gray-1"
              benchmark={cac?.firstTime}
              customerType="firstTime"
              showCustomBenchmarks={showNbCalculatedBenchmarks}
              benchmarkType={BenchmarkTypeEnum.CAC}
              nbCalculatedBenchmarks={nbCalculatedBenchmarks}
            />
            <BenchmarkTableCell
              className="text-right"
              benchmark={cac?.returning}
              customerType="returning"
              showCustomBenchmarks={showNbCalculatedBenchmarks}
              benchmarkType={BenchmarkTypeEnum.CAC}
              nbCalculatedBenchmarks={nbCalculatedBenchmarks}
            />
          </div>
          <div className="table-row-group bg-[#FAFAFF] text-sm rounded-lg">
            <BodyTableCell className="lext-left font-bold border-r border-gray-1 rounded-bl-lg">
              <div>ROAS</div>
              {showNbCalculatedBenchmarks && (
                <div className="text-[11px] leading-3 font-light">
                  (NB Benchmark ROAS)
                </div>
              )}
            </BodyTableCell>
            <BenchmarkTableCell
              className="text-right border-r border-gray-1"
              benchmark={roas?.blended}
              customerType="blended"
              showCustomBenchmarks={showNbCalculatedBenchmarks}
              benchmarkType={BenchmarkTypeEnum.ROAS}
              nbCalculatedBenchmarks={nbCalculatedBenchmarks}
            />
            <BenchmarkTableCell
              className="text-right border-r border-gray-1"
              benchmark={roas?.firstTime}
              customerType="firstTime"
              showCustomBenchmarks={showNbCalculatedBenchmarks}
              benchmarkType={BenchmarkTypeEnum.ROAS}
              nbCalculatedBenchmarks={nbCalculatedBenchmarks}
            />
            <BenchmarkTableCell
              className="text-right rounded-br-lg"
              benchmark={roas?.returning}
              customerType="returning"
              showCustomBenchmarks={showNbCalculatedBenchmarks}
              benchmarkType={BenchmarkTypeEnum.ROAS}
              nbCalculatedBenchmarks={nbCalculatedBenchmarks}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
